'use client';

import { useState, useEffect } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import Breadcrumb from '@/components/Breadcrumbs/Breadcrumb';
import { ShowcaseSection } from '@/components/Layouts/showcase-section';
import { Select } from '@/components/FormElements/select';
import { TextArea } from '@/components/FormElements/textarea';
import { BookingTimeline } from '@/components/ui/BookingTimeline';
import { BookingSummary } from '@/components/ui/BookingSummary';
import {
  Booking,
  BookingStatus,
  BookingPaymentStatus,
  BOOKING_STATUSES,
  BOOKING_PAYMENT_STATUSES,
  PaymentMethod,
  PAYMENT_METHODS,
} from '@/lib/bookings/types';
import {
  getBookingById,
  updateBooking,
  updateBookingStatus,
  updateBookingPaymentStatus,
  deleteBooking,
  confirmBooking,
  processBookingPayment,
  ProcessPaymentParams,
} from '@/lib/bookings/api';

export default function BookingDetailPage() {
  const params = useParams();
  const router = useRouter();
  const bookingId = params.id as string;

  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [description, setDescription] = useState('');
  
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(PaymentMethod.CREDIT_CARD);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [status, setStatus] = useState<BookingStatus>(BookingStatus.PENDING);
  const [paymentStatus, setPaymentStatus] = useState<BookingPaymentStatus>(
    BookingPaymentStatus.UNPAID
  );

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      setLoading(true);
      const response = await getBookingById(bookingId);
      if (response?.data) {
        const bookingData = response.data;
        setBooking(bookingData);
        setDescription(bookingData.description || '');
        setStatus(bookingData.status);
        setPaymentStatus(bookingData.paymentStatus);
      }
    } catch (err) {
      console.error('Error fetching booking:', err);
      setError('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: BookingStatus) => {
    try {
      setSaving(true);
      await updateBookingStatus(bookingId, newStatus);
      setStatus(newStatus);
      setSuccess('Booking status updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update booking status');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setSaving(true);
      await confirmBooking(bookingId);
      setStatus(BookingStatus.CONFIRMED);
      setSuccess('Booking confirmed successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error confirming booking:', err);
      setError('Failed to confirm booking');
    } finally {
      setSaving(false);
    }
  };

  const handlePayment = async () => {
    try {
      setSaving(true);
      const paymentParams: ProcessPaymentParams = {
        paymentMethod,
        reference: paymentReference || undefined,
        notes: paymentNotes || undefined,
      };
      if (booking && paymentAmount > 0 && paymentAmount < Number(booking.totalAmount)) {
        paymentParams.amount = paymentAmount;
      }
      await processBookingPayment(bookingId, paymentParams);
      setShowPaymentModal(false);
      await fetchBooking(); // Refresh booking data
      setSuccess('Payment processed successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error processing payment:', err);
      setError('Failed to process payment');
    } finally {
      setSaving(false);
    }
  };

  const openPaymentModal = () => {
    if (!booking) return;
    setPaymentAmount(Number(booking.totalAmount));
    setPaymentMethod(PaymentMethod.CREDIT_CARD);
    setPaymentReference('');
    setPaymentNotes('');
    setShowPaymentModal(true);
  };

  const handlePaymentStatusUpdate = async (newPaymentStatus: BookingPaymentStatus) => {
    try {
      setSaving(true);
      await updateBookingPaymentStatus(bookingId, newPaymentStatus);
      setPaymentStatus(newPaymentStatus);
      setSuccess('Payment status updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating payment status:', err);
      setError('Failed to update payment status');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateBooking(bookingId, { description });
      setSuccess('Booking updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating booking:', err);
      setError('Failed to update booking');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this booking?')) return;

    try {
      setSaving(true);
      await deleteBooking(bookingId);
      router.push('/bookings');
    } catch (err) {
      console.error('Error deleting booking:', err);
      setError('Failed to delete booking');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusColor = (bookingStatus: BookingStatus) => {
    const statusConfig = BOOKING_STATUSES.find((s) => s.value === bookingStatus);
    switch (statusConfig?.color) {
      case 'green':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'yellow':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'red':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'blue':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getPaymentStatusColor = (payStatus: BookingPaymentStatus) => {
    const statusConfig = BOOKING_PAYMENT_STATUSES.find((s) => s.value === payStatus);
    switch (statusConfig?.color) {
      case 'green':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'yellow':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'red':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getModuleBadge = (moduleType?: string) => {
    if (!moduleType) return null;
    const colors: Record<string, string> = {
      stay: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
      trip: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
    };
    return (
      <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ${colors[moduleType] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`}>
        {moduleType === 'stay' ? '🏨 Stay' : '✈️ Trip'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-body text-gray-600 dark:text-gray-400">Loading booking details...</p>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="space-y-6">
        <Breadcrumb pageName="Booking Not Found" />
        <div className="rounded-lg bg-red-100 p-4 text-red-800 dark:bg-red-900/20 dark:text-red-400">
          Booking not found
        </div>
        <Link href="/bookings">
          <button className="inline-flex rounded-lg border border-stroke px-6 py-3 font-medium text-dark hover:bg-gray-2 dark:border-dark-3 dark:text-white dark:hover:bg-dark">
            Back to Bookings
          </button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumb pageName={`Booking ${booking.bookingNumber}`} />

      {error && (
        <div className="rounded-lg bg-red-100 p-4 text-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {success && (
        <div className="rounded-lg bg-green-100 p-4 text-green-800 dark:bg-green-900/20 dark:text-green-400">
          {success}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span
            className={`rounded-full px-4 py-2 text-sm font-semibold ${getStatusColor(
              booking.status
            )}`}
          >
            {booking.status}
          </span>
          <span
            className={`rounded-full px-4 py-2 text-sm font-semibold ${getPaymentStatusColor(
              booking.paymentStatus
            )}`}
          >
            {booking.paymentStatus.replace('_', ' ')}
          </span>
          {getModuleBadge((booking as any).moduleType)}
        </div>
        <div className="flex items-center gap-3">
          <Link href={`/bookings/${bookingId}/edit`}>
            <button className="inline-flex rounded-lg border border-primary bg-primary px-4 py-2 font-medium text-white hover:bg-opacity-90">
              Edit Booking
            </button>
          </Link>
          <Link href="/bookings">
            <button className="inline-flex rounded-lg border border-stroke px-4 py-2 font-medium text-dark hover:bg-gray-2 dark:border-dark-3 dark:text-white dark:hover:bg-dark">
              Back
            </button>
          </Link>
          <button
            onClick={handleDelete}
            disabled={saving}
            className="inline-flex rounded-lg bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      </div>

      <div className="mb-6">
        <BookingSummary booking={booking} moduleType={(booking as any).moduleType} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ShowcaseSection title="Booking Information" className="!p-5.5">
          <div className="space-y-4">
            <div className="flex justify-between border-b border-stroke pb-3 dark:border-dark-3">
              <span className="text-gray-500">Booking Number</span>
              <span className="font-semibold text-dark dark:text-white">
                {booking.bookingNumber}
              </span>
            </div>
            <div className="flex justify-between border-b border-stroke pb-3 dark:border-dark-3">
              <span className="text-gray-500">Customer ID</span>
              <span className="font-mono text-sm text-dark dark:text-white">
                {booking.customerId}
              </span>
            </div>
            <div className="flex justify-between border-b border-stroke pb-3 dark:border-dark-3">
              <span className="text-gray-500">Check-in</span>
              <span className="text-dark dark:text-white">
                {booking.startDate ? formatDate(booking.startDate) : '-'}
              </span>
            </div>
            <div className="flex justify-between border-b border-stroke pb-3 dark:border-dark-3">
              <span className="text-gray-500">Check-out</span>
              <span className="text-dark dark:text-white">
                {booking.endDate ? formatDate(booking.endDate) : '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Created</span>
              <span className="text-dark dark:text-white">
                {formatDateTime(booking.createdAt)}
              </span>
            </div>
          </div>
        </ShowcaseSection>

        <ShowcaseSection title="Financial Summary" className="!p-5.5">
          <div className="space-y-4">
            <div className="flex justify-between border-b border-stroke pb-3 dark:border-dark-3">
              <span className="text-gray-500">Subtotal</span>
              <span className="font-semibold text-dark dark:text-white">
                {formatCurrency((booking as any).subtotal || booking.totalAmount)}
              </span>
            </div>
            <div className="flex justify-between border-b border-stroke pb-3 dark:border-dark-3">
              <span className="text-gray-500">Taxes</span>
              <span className="font-semibold text-dark dark:text-white">
                {formatCurrency((booking as any).taxes || 0)}
              </span>
            </div>
            <div className="flex justify-between border-b border-stroke pb-3 dark:border-dark-3">
              <span className="text-gray-500">Discount</span>
              <span className="font-semibold text-red-600 dark:text-red-400">
                -{formatCurrency((booking as any).discountAmount || 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-lg font-semibold text-dark dark:text-white">Total</span>
              <span className="text-2xl font-bold text-primary">
                {formatCurrency(booking.totalAmount)}
              </span>
            </div>
          </div>
        </ShowcaseSection>
      </div>

      <ShowcaseSection title="Description & Notes" className="!p-5.5">
        <TextArea
          label=""
          placeholder="Add notes about this booking..."
          value={description}
          handleChange={(e) => setDescription(e.target.value)}
        />
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-center font-medium text-white hover:bg-opacity-90 transition duration-200 ease-in-out disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </ShowcaseSection>

      <ShowcaseSection title="Quick Actions" className="!p-5.5">
        <div className="flex flex-wrap gap-3">
          {/* Issue / Confirm Button - uses new /confirm endpoint */}
          {booking.status === BookingStatus.PENDING && (
            <button
              onClick={handleConfirm}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2 font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              📝 Issue / Confirm
            </button>
          )}
          
          {/* Process Payment Button */}
          {booking.status !== BookingStatus.CANCELLED &&
           booking.status !== BookingStatus.COMPLETED &&
           booking.paymentStatus !== BookingPaymentStatus.PAID &&
           booking.paymentStatus !== BookingPaymentStatus.REFUNDED && (
            <button
              onClick={openPaymentModal}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              💳 Process Payment
            </button>
          )}
          
          {/* Void Button - same as Cancel */}
          {booking.status !== BookingStatus.CANCELLED &&
            booking.status !== BookingStatus.COMPLETED && (
              <button
                onClick={() => handleStatusUpdate(BookingStatus.CANCELLED)}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-600 px-4 py-2 font-medium text-white hover:bg-orange-700 disabled:opacity-50"
              >
                🚫 Void
              </button>
            )}
          
          {/* Cancel Button */}
          {booking.status !== BookingStatus.CANCELLED &&
            booking.status !== BookingStatus.COMPLETED && (
              <button
                onClick={() => handleStatusUpdate(BookingStatus.CANCELLED)}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                ✕ Cancel
              </button>
            )}
          
          {/* Mark Completed */}
          {booking.status === BookingStatus.CONFIRMED && (
            <button
              onClick={() => handleStatusUpdate(BookingStatus.COMPLETED)}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              ✓ Mark Completed
            </button>
          )}
          
          {/* Refund Button */}
          {booking.paymentStatus !== BookingPaymentStatus.REFUNDED && (
            <button
              onClick={() => handlePaymentStatusUpdate(BookingPaymentStatus.REFUNDED)}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2 font-medium text-white hover:bg-purple-700 disabled:opacity-50"
            >
              💰 Refund
            </button>
          )}
        </div>
      </ShowcaseSection>

      <ShowcaseSection title="Booking Timeline" className="!p-5.5">
        <BookingTimeline booking={booking} />
      </ShowcaseSection>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-dark">
            <h3 className="mb-4 text-xl font-semibold text-dark dark:text-white">
              Process Payment
            </h3>
            
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Payment Method
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                className="w-full rounded-lg border border-stroke bg-transparent px-4 py-2 text-dark focus:border-primary focus:outline-none dark:border-dark-3 dark:text-white"
              >
                {PAYMENT_METHODS.map((method) => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Amount
              </label>
              <input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(Number(e.target.value))}
                min={0}
                max={Number(booking.totalAmount)}
                step={0.01}
                className="w-full rounded-lg border border-stroke bg-transparent px-4 py-2 text-dark focus:border-primary focus:outline-none dark:border-dark-3 dark:text-white"
              />
              <p className="mt-1 text-sm text-gray-500">
                Total: {formatCurrency(booking.totalAmount)} | Remaining: {formatCurrency(Number(booking.totalAmount))}
              </p>
            </div>

            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Reference (optional)
              </label>
              <input
                type="text"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="Transaction ID, receipt number, etc."
                className="w-full rounded-lg border border-stroke bg-transparent px-4 py-2 text-dark focus:border-primary focus:outline-none dark:border-dark-3 dark:text-white"
              />
            </div>

            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Notes (optional)
              </label>
              <textarea
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                rows={2}
                placeholder="Payment notes..."
                className="w-full rounded-lg border border-stroke bg-transparent px-4 py-2 text-dark focus:border-primary focus:outline-none dark:border-dark-3 dark:text-white"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="rounded-lg border border-stroke px-4 py-2 font-medium text-dark hover:bg-gray-2 dark:border-dark-3 dark:text-white dark:hover:bg-dark"
              >
                Cancel
              </button>
              <button
                onClick={handlePayment}
                disabled={saving || paymentAmount <= 0}
                className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Processing...' : 'Process Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {booking.items && booking.items.length > 0 && (
        <ShowcaseSection title="Booking Items" className="overflow-hidden !p-0">
          <table className="w-full">
            <thead className="border-b border-stroke bg-gray-2 dark:border-dark-3 dark:bg-dark">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white">
                  Resource
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white">
                  Dates
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white">
                  Qty
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white">
                  Unit Price
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {booking.items.map((item, index) => (
                <tr
                  key={item.id || index}
                  className="border-b border-stroke hover:bg-gray-2 dark:border-dark-3 dark:hover:bg-dark-2"
                >
                  <td className="px-4 py-3 text-sm text-dark dark:text-white">
                    {item.resourceName}
                  </td>
                  <td className="px-4 py-3 text-sm text-dark-6 dark:text-gray-5">
                    {formatDate(item.startDate)} - {formatDate(item.endDate)}
                  </td>
                  <td className="px-4 py-3 text-sm text-dark-6 dark:text-gray-5">
                    {item.quantity}
                  </td>
                  <td className="px-4 py-3 text-sm text-dark-6 dark:text-gray-5">
                    {formatCurrency(item.unitPrice)}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-dark dark:text-white">
                    {formatCurrency(item.totalPrice)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ShowcaseSection>
      )}

      {booking.travellers && booking.travellers.length > 0 && (
        <ShowcaseSection title="Travellers" className="overflow-hidden !p-0">
          <table className="w-full">
            <thead className="border-b border-stroke bg-gray-2 dark:border-dark-3 dark:bg-dark">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white">
                  Passport
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white">
                  Category
                </th>
              </tr>
            </thead>
            <tbody>
              {booking.travellers.map((traveller, index) => (
                <tr
                  key={traveller.id || index}
                  className="border-b border-stroke hover:bg-gray-2 dark:border-dark-3 dark:hover:bg-dark-2"
                >
                  <td className="px-4 py-3 text-sm text-dark dark:text-white">
                    {traveller.title ? `${traveller.title} ` : ''}{traveller.firstName} {traveller.lastName}
                  </td>
                  <td className="px-4 py-3 text-sm text-dark-6 dark:text-gray-5">
                    {traveller.email || '-'}
                    {traveller.phone && <span className="ml-2">• {traveller.phone}</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-dark-6 dark:text-gray-5">
                    {traveller.passportNumber || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-dark-6 dark:text-gray-5">
                    <span className="capitalize">{traveller.ageCategory}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ShowcaseSection>
      )}
    </div>
  );
}
